import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/**
 * Pipeline "Share My Perspective" RTK Query slice.
 *
 * Three endpoints, all rooted at /pipeline-drugs/:slug/perspective…:
 *   - POST .../perspective          → submit/upsert one perspective
 *   - GET  .../perspective/mine     → pre-fill the form on revisit
 *   - GET  .../perspective-summary  → aggregated counts for the public
 *                                     detail-page widget
 *
 * Auth pattern mirrors productsApi: pull the `token` cookie set by the
 * existing login flow and attach it as `Authorization: Bearer …`.
 */
export const pipelinePerspectiveApi = createApi({
  reducerPath: "pipelinePerspectiveApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/pipeline-drugs/`,
    prepareHeaders: (headers) => {
      if (typeof document === "undefined") return headers;
      const cookies = document.cookie.split(";");
      for (const c of cookies) {
        const t = c.trim();
        if (t.startsWith("token=")) {
          headers.set("authorization", `Bearer ${t.slice("token=".length)}`);
          break;
        }
      }
      return headers;
    },
  }),
  // Tag for cache invalidation. `MySubmitted-${slug}` lets us blow away
  // a specific drug's "mine" + "summary" cache after a successful POST.
  tagTypes: ["PipelinePerspective"],
  endpoints: (builder) => ({
    submitPipelinePerspective: builder.mutation({
      query: ({ slug, ...body }) => ({
        url: `${encodeURIComponent(slug)}/perspective`,
        method: "POST",
        body,
      }),
      // After a successful submit, force re-fetch of the user's own
      // perspective (so the form pre-fills correctly on re-open) and
      // the public summary widget on the detail page.
      invalidatesTags: (_result, _err, arg) => [
        { type: "PipelinePerspective", id: `mine-${arg?.slug}` },
        { type: "PipelinePerspective", id: `summary-${arg?.slug}` },
      ],
    }),

    getMyPipelinePerspective: builder.query({
      query: (slug) => `${encodeURIComponent(slug)}/perspective/mine`,
      providesTags: (_result, _err, slug) => [
        { type: "PipelinePerspective", id: `mine-${slug}` },
      ],
      // Soft-fail: the BE already 200s with `perspective: null` when
      // not found or on internal error, so we just unwrap.
      transformResponse: (resp) => resp?.perspective || null,
    }),

    getPipelinePerspectiveSummary: builder.query({
      query: (slug) => `${encodeURIComponent(slug)}/perspective-summary`,
      providesTags: (_result, _err, slug) => [
        { type: "PipelinePerspective", id: `summary-${slug}` },
      ],
      transformResponse: (resp) => resp?.summary || null,
    }),
  }),
});

export const {
  useSubmitPipelinePerspectiveMutation,
  useGetMyPipelinePerspectiveQuery,
  useGetPipelinePerspectiveSummaryQuery,
} = pipelinePerspectiveApi;
